import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";

import { VStack, StackDivider } from "@chakra-ui/react";

import { getSeoMeta, getBaseUrl, getDefaultOgImage } from "#app/utils/meta.js";
import {
  LANDING_DATA_CACHE_CONTROL,
  getHomepageData,
} from "#app/components/landing/landingLoader.server.js";
import {
  FreeToTrySection,
  HomeHero,
  HowItWorksVideo,
  NotificationProof,
  ReviewSection,
  WhyCancellations,
} from "#app/components/index.jsx";

export function meta({ matches }) {
  const baseUrl = getBaseUrl(matches);
  return getSeoMeta({
    title: "Campground fully booked? Schnerp it!",
    description:
      "Tell us where and when you want to camp, and we'll notify you if a site opens up. It's completely free to try.",
    url: baseUrl,
    image: getDefaultOgImage(baseUrl),
  });
}

export async function loader({ request }) {
  // Bots/monitors hit HEAD / frequently; serve an empty shell without
  // touching Postgres. HEAD responses have no body anyway.
  if (request.method === "HEAD") {
    return json({
      recentOpenings: [],
      reviews: [],
      stats: {},
      alertsSent: null,
    });
  }

  // Served from process-wide caches (see getHomepageData) so a burst of
  // homepage requests costs one query per TTL, not two per request.
  const { recentOpenings, reviews, stats, alertsSent } =
    await getHomepageData();
  // Cache-Control is applied only to Remix `?_data=` responses (client-side
  // navigations). Without a `headers` export, Remix does NOT forward loader
  // headers to the HTML document, so first-render SSR stays fresh and the
  // root loader's cookie/locale variation doesn't leak across users.
  return json(
    { recentOpenings, reviews, stats, alertsSent },
    { headers: { "Cache-Control": LANDING_DATA_CACHE_CONTROL } },
  );
}

export default function Index() {
  const { recentOpenings, reviews, stats, alertsSent } = useLoaderData();

  return (
    <VStack spacing={{ base: 10, md: 16 }} divider={<StackDivider />}>
      <HomeHero
        heading="Campground"
        subtitle="We watch reservation websites for cancellations and notify you the second a spot opens up. It's totally free to try."
        showCountryPills
        reviews={reviews}
        stats={stats}
      />
      {/* The infomercial does the talking — featured right after the hero.
          Steps, subtitle, and the video itself are component defaults now. */}
      <HowItWorksVideo />
      {/* Proof it works: a feed of the real alerts we've sent, the way
          campers receive them. `ctaTo` defaults to "/new". */}
      <NotificationProof openings={recentOpenings} alertsSent={alertsSent} />
      <ReviewSection reviews={reviews} stats={stats} />
      <WhyCancellations />
      {/* Free-to-try is the closing CTA, matching every landing page. */}
      <FreeToTrySection />
    </VStack>
  );
}
